import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence  } from 'framer-motion';
import { Upload, AlertCircle, X } from 'lucide-react';
import { S } from './forms/styles.js';
import { validateFileType, formatFileSize } from './forms/EnrolmentForms/utils.js';

// Grid components
export function Row({ children, className = "" }) {
  return (
    <div style={S.row} className={className}>
      {children}
    </div>
  );
}

export function Col({ span = 12, children, className = "" }) {
  return (
    <div style={S.col(span)} className={className}>
      {children}
    </div>
  );
}

// Label component
export function L({ htmlFor, required = false, children }) {
  return (
    <label htmlFor={htmlFor} style={S.label}>
      {children}
      {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
    </label>
  );
}

// Enhanced Input component with validation states
export function Input({ 
  error, 
  success,
  onFocus,
  onBlur,
  ...props 
}) {
  const [isFocused, setIsFocused] = useState(false);

  const inputStyle = {
    ...S.input,
    ...(error ? S.inputError : {}),
    ...(isFocused ? S.inputFocus : {}),
    ...(props.style || {})
  };

  return (
    <div>
      <input
        {...props}
        style={inputStyle}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
      />
      {error && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={S.error}
        >
          <AlertCircle size={16} />
          {error}
        </motion.div>
      )}
    </div>
  );
}

// Enhanced Select component
export function Select({ error, children, ...props }) {
  return (
    <div>
      <select
        {...props}
        style={{
          ...S.select,
          ...(error ? S.inputError : {}),
          ...(props.style || {})
        }}
      >
        {children}
      </select>
      {error && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={S.error}
        >
          <AlertCircle size={16} />
          {error}
        </motion.div>
      )}
    </div>
  );
}

// Enhanced Textarea component
export function Textarea({ error, ...props }) {
  return (
    <div>
      <textarea
        {...props}
        style={{
          ...S.textarea,
          ...(error ? S.inputError : {}),
          ...(props.style || {})
        }}
      />
      {error && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={S.error}
        >
          <AlertCircle size={16} />
          {error}
        </motion.div>
      )}
    </div>
  );
}

// Enhanced File Upload component
export function FileInput({
  value,
  onChange,
  multiple = false,
  accept = "image/*,.pdf",
  maxSize = 2 * 1024 * 1024,
  error,
  placeholder = "Choose files or drag and drop"
}) {
  const [dragOver, setDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const files = Array.isArray(value) ? value : value ? [value] : [];

  const handleFileChange = (newFiles) => {
    const fileArray = Array.from(newFiles);
    const validFiles = [];
    const errors = [];

    fileArray.forEach(file => {
      if (file.size > maxSize) errors.push(`${file.name} is too large`);
      else if (!validateFileType(file)) errors.push(`${file.name} unsupported type`);
      else validFiles.push(file);
    });

    setValidationErrors(errors);

    let finalFiles = multiple ? [...files, ...validFiles] : validFiles.slice(0, 1);
    onChange?.(multiple ? finalFiles : finalFiles[0] || null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileChange(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    onChange?.(multiple ? updatedFiles : null);
  };
  const handleFileClick = (file) => {
    if (file.url) {
      window.open(file.url, "_blank");
    } else {
      alert("File is not saved.");
    }
  };

  return (
    <div>
      <div
        style={{
          ...S.fileUpload,
          borderColor: dragOver ? "#667eea" : error ? "#ef4444" : "#d1d5db",
          background: dragOver ? "#f8faff" : "#fafafa",
          position: 'relative'
        }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={(e) => handleFileChange(e.target.files)}
          style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
        />
        <Upload size={24} color="#6b7280" />
        <p style={{ margin: '8px 0 4px', color: '#374151', fontWeight: 500 }}>{placeholder}</p>
        <p style={S.hint}>Supported: PDF, JPG, PNG • Max size: {formatFileSize(maxSize)}</p>
      </div>

      <AnimatePresence>
        {files.map((file, idx) => (
          <motion.div
            key={file.name + idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 12px',
              marginTop: 8,
              background: '#f3f4f6',
              borderRadius: 8,
              fontSize: 14
            }}
          >
            <span 
                style={{ flex: 1, marginRight: 8, cursor: 'pointer', textDecoration: file.url ? 'underline' : 'none', color: file.url ? '#1d4ed8' : '#374151' }}
                onClick={() => handleFileClick(file)}
              >
                {file.name} {file.size ? `(${formatFileSize(file.size)})` : ""}
              </span>
            {/* <span>{file.name} {file.size ? `(${formatFileSize(file.size)})` : ""}</span> */}
            <button type="button" onClick={() => removeFile(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
              <X size={16} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>

      {error && (
        <div style={S.error}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {validationErrors.length > 0 && (
        <div style={S.error}>
          <AlertCircle size={16} />
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}



// Section Title component with icon support
export function SectionTitle({ children, icon: Icon, step }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      style={S.h2}
    >
      {Icon && <Icon size={24} />}
      {step && <span style={{ 
        fontSize: 16, 
        fontWeight: 500, 
        color: '#6b7280',
        marginRight: 8 
      }}>
        {step}.
      </span>}
      {children}
    </motion.div>
  );
}

// Checkbox component (stable across single & multi-line labels)
export function Checkbox({ 
  checked, 
  onChange, 
  children, 
  error,
  ...props 
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start", // ensures consistent alignment for multi-line text
        cursor: "pointer",
        gap: 10, // balanced space between checkbox and label text
        lineHeight: 1.5,
        userSelect: "none",
      }}
    >
      {/* Checkbox wrapper (fixed area) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
          flexShrink: 0, // keeps size fixed
          marginTop: 2, // subtle balance for text baseline
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          {...props}
          style={{
            width: 18,
            height: 18,
            margin: 0,
            cursor: "pointer",
            accentColor: error ? "#ef4444" : "#2563eb",
          }}
        />
      </div>

      {/* Label text */}
      <span
        style={{
          fontSize: 14,
          color: error ? "#ef4444" : "#374151",
          lineHeight: "1.5",
          marginTop: 1, // optical tweak so text and checkbox align better
        }}
      >
        {children}
      </span>
    </label>
  );
}





// Radio button component
export function Radio({ 
  checked, 
  onChange, 
  children, 
  name,
  value,
  ...props 
}) {
  return (
    <label style={S.checkbox}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        {...props}
        style={{
          width: 16,
          height: 16,
          marginRight: 8,
          cursor: 'pointer'
        }}
      />
      <span style={{ fontSize: 14, color: '#374151' }}>
        {children}
      </span>
    </label>
  );
}

// Enhanced Button component
export function Button({ 
  variant = "primary", 
  size = "md",
  loading = false,
  disabled = false,
  icon: Icon,
  children,
  onClick,
  ...props 
}) {
  const buttonStyle = {
    ...S.button(variant, size),
    ...(disabled || loading ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
    ...(props.style || {})
  };

  return (
    <motion.button
      {...props}
      style={buttonStyle}
      onClick={disabled || loading ? undefined : onClick}
      whileHover={disabled || loading ? {} : { 
        scale: 1.02,
        boxShadow: variant === "primary" 
          ? "0 8px 25px rgba(102, 126, 234, 0.3)"
          : "0 4px 12px rgba(0,0,0,0.1)"
      }}
      whileTap={disabled || loading ? {} : { scale: 0.98 }}
      disabled={disabled || loading}
    >
      {loading && (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          style={{ width: 16, height: 16, marginRight: Icon ? 0 : 8 }}
        >
          ⟳
        </motion.div>
      )}
      {Icon && !loading && <Icon size={16} />}
      {children}
    </motion.button>
  );
}

export function FormField({ label, required = false, error, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <L required={required}>{label}</L>}
      {children}
      {hint && !error && <p style={S.hint}>{hint}</p>}
      {error && (
        <motion.div
          initial={false}  // prevent re-animation on every render
          animate={{ opacity: 1 }}
          style={S.error}
        >
          {error}
        </motion.div>
      )}
    </div>
  );
}

export function SignatureInput({
  value,
  onChange,
  error,
  placeholder = "Draw or type your signature",
  readOnly = false
}) {
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);

  // Get correct coordinates even when canvas is resized on mobile
  const getX = (e, rect) => {
    const clientX = e.touches
      ? e.touches[0].clientX
      : e.clientX;

    return (
      (clientX - rect.left) *
      (canvasRef.current.width / rect.width)
    );
  };

  const getY = (e, rect) => {
    const clientY = e.touches
      ? e.touches[0].clientY
      : e.clientY;

    return (
      (clientY - rect.top) *
      (canvasRef.current.height / rect.height)
    );
  };

  // Draw start
  const startDraw = (e) => {
    if (readOnly) return;

    // Prevent phone screen from scrolling while drawing
    if (e.cancelable) {
      e.preventDefault();
    }

    setIsDrawing(true);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();

    ctx.beginPath();
    ctx.moveTo(
      getX(e, rect),
      getY(e, rect)
    );
  };

  // Drawing
  const draw = (e) => {
    if (!isDrawing || readOnly) return;

    // Prevent phone screen from moving while drawing
    if (e.cancelable) {
      e.preventDefault();
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();

    ctx.lineTo(
      getX(e, rect),
      getY(e, rect)
    );

    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  };

  // Stop drawing
  const stopDraw = (e) => {
    if (readOnly) return;

    if (e?.cancelable) {
      e.preventDefault();
    }

    setIsDrawing(false);

    if (canvasRef.current) {
      const dataUrl =
        canvasRef.current.toDataURL("image/png");

      onChange?.(dataUrl);
    }
  };

  // Clear canvas
  const clearCanvas = () => {
    if (readOnly) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    onChange?.("");
  };

  // Load existing signature
  useEffect(() => {
    if (value && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      const img = new Image();
      img.src = value;

      img.onload = () => {
        ctx.clearRect(
          0,
          0,
          canvas.width,
          canvas.height
        );

        ctx.drawImage(
          img,
          0,
          0,
          canvas.width,
          canvas.height
        );
      };
    }
  }, [value]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        width: "100%",
      }}
    >
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        style={{
          border: "1px solid #ccc",
          borderRadius: 8,

          // Responsive
          width: "100%",
          maxWidth: "400px",
          height: "150px",

          // IMPORTANT FOR MOBILE
          touchAction: "none",

          cursor: readOnly
            ? "not-allowed"
            : "crosshair",
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={stopDraw}
        onMouseLeave={stopDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={stopDraw}
      />

      {!readOnly && (
        <div
          style={{
            display: "flex",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={clearCanvas}
          >
            Clear
          </button>
        </div>
      )}

      {error && (
        <motion.div
          initial={{
            opacity: 0,
            y: -10,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          style={S.error}
        >
          <AlertCircle size={16} />
          {error}
        </motion.div>
      )}
    </div>
  );
}
// -----------------------------
  // Status Badge
  // -----------------------------
export const StatusBadge = ({ status }) => {
  const statusColors = {
    active: "var(--color-primary)",
    pending: "orange",
    new: "green",
    suspended: "var(--color-accent-gold)",
    approved: "green",
    resolved: "gray",
    reported: "gray",
    rejected: "red",
    inactive: "grey",
    overdue: "red",
    paid: "green",
  };
  return (
    <span
      className="status-badge"
      style={{ backgroundColor: status != undefined ? statusColors[status.toLowerCase()] : "gray" }}
    >
      {status}
    </span>
  );
};
// Badge component
export const SectionBadge = ({ label, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
    <div
      style={{
        background: color || "#3b82f6",
        color: "white",
        borderRadius: "50%",
        width: 24,
        height: 24,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: 12,
        fontWeight: "bold",
      }}
    >
      {label}
    </div>
    <h3 style={{ margin: 0, fontSize: 16 }}>{label}</h3>
  </div>
);

export const Modal = ({ title, children, onClose }) => {
  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 8,
        width: "400px",
        maxWidth: "90%",
        padding: 24,
        position: "relative"
      }}>
        <h2>{title}</h2>
        <div>{children}</div>
        <button
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            fontSize: 18,
            cursor: "pointer"
          }}
          onClick={onClose}
        >
          ×
        </button>
      </div>
    </div>
  );
};